/**
 * MC4 S3 — pure OSNAP-resolution + hover-hit-test step for `onPointerMove`.
 *
 * Extracted from `stageInteractions.ts` (1479 LOC, over the 500 budget). This
 * module owns ALL the per-move snap/hover LOGIC and returns plain DATA; the
 * caller (`stageInteractions.onPointerMove`) applies every side-effect
 * (`setOverlayState`, the selection-cycling zustand store, `onCanvasHover`,
 * `lastSnappedWorld`). Keeping it pure makes the in-VP routing unit-testable
 * with a stubbed spatial index + a real `resolveActiveInputSpace`-built space,
 * exactly like the S2 pick/drag mirror in `scene/StagePointerHandlers.ts`.
 *
 * # MC4 S3 — input-space routing (the core)
 *
 * Today the snap resolver runs against the PAPER-camera unprojected cursor, so
 * inside an UNLOCKED active viewport (MSPACE_VIA_VP) it snaps to nothing (or to
 * invisible paper geometry). This step mirrors the S2 pick/drag fix: it routes
 * the snap + hover hit-test through the shared `ActiveInputSpace` resolver so
 * the spatial query runs in the VP's MODEL space, only model-space candidates
 * (and not VP-frozen layers) qualify, the aperture/tolerance use the VP model
 * scale, and the marker glyph is mapped back to PAPER coords (it rides the
 * paper camera). Outside a VP the resolver returns the IDENTITY space
 * (`toSpace`/`fromSpace` no-ops, `pixelsPerWorld === paperPpw`,
 * `candidateEligible === entitySpaceId === fallbackSpaceId`), so the behaviour
 * is byte-identical to the legacy path — the ONLY intentional identity-mode
 * delta is that hover now also respects `candidateEligible` (the same filter S2
 * already added for pick on layouts).
 *
 * **THREE-free.** `resolveObjectSnap` and the distance refinement are
 * THREE-free; this module imports nothing from `three`.
 */

import {
  resolveObjectSnap,
  AUTOSNAP_MARKER,
  AUTOSNAP_TOOLTIP,
  AUTOSNAP_MAGNET,
} from "./resolveObjectSnap";
import { EXTENDED_OVERRIDE_LABEL } from "./temporaryOverride";
import type {
  ExtendedOverrideMode,
  ExtendedOverrideState,
  OsnapMode,
} from "./osnapTypes";
import type { RenderableEntity, SpatialIndex, Vec2 } from "../types";
import type { ActiveInputSpace } from "../viewports/activeInputSpace";
import type { SnapMarkerMode } from "../overlays/snapMarker";
import { refinePickCandidates } from "../raycast/pick";
import { compareTopmostFirst } from "../raycast/candidateOrdering";
import { makeVisibleChildGeometryFilter } from "../layerChainVisibility";

/**
 * OSNAP resolver configuration ref payload. The host (`Scene3DStage`) updates
 * the live ref every frame from the sysvar store so the resolver always reads
 * fresh OSMODE / APERTURE / AUTOSNAP values. When `activeModes` is empty
 * (OSMODE = 0) the resolver short-circuits and the cursor-follow snap path is
 * fully disabled — matching AutoCAD parity.
 *
 * `getEntityById` lifts a spatial-index hit id back to a full RenderableEntity
 * (the spatial index already returns full entities for the standard adapter,
 * but the cache-direct path may return stub records — the resolver falls back
 * to this getter for those).
 *
 * Moved here from `stageInteractions.ts` (MC4 S3 extraction) so the pure snap
 * step owns its own contract; `stageInteractions.ts` re-exports it so existing
 * import paths (`scene/StageInteractionsBinder.ts`,
 * `scene/useOsnapBindings.ts`) keep working unchanged.
 */
export interface OsnapBindings {
  readonly activeModes: ReadonlySet<OsnapMode>;
  readonly aperturePx: number;
  readonly autosnap: number;
  readonly getEntityById: (id: string) => RenderableEntity | undefined;
  /**
   * OSNAP-OVERRIDE-MODES — the live Extended (Apparent) Intersection override,
   * if one is armed. Threaded into `resolveObjectSnap`; when it carries a
   * captured first operand it supersedes the running snaps for the pick. Absent
   * / `null` → the normal running-snap path.
   */
  readonly extendedOverride?: ExtendedOverrideState | null;
}

/**
 * Snap mode label for the SnapTip tooltip. Mirrors AutoCAD's labels shown next
 * to the marker glyph when AUTOSNAP bit 2 is on. Kept in English here because
 * i18n is the parent component's responsibility.
 */
export const OSNAP_MODE_LABEL: Record<OsnapMode, string> = {
  endpoint: "Endpoint",
  midpoint: "Midpoint",
  intersection: "Intersection",
  appint: "Apparent Intersection",
  center: "Center",
  geomcenter: "Geometric Center",
  quadrant: "Quadrant",
  tangent: "Tangent",
  perpendicular: "Perpendicular",
  node: "Node",
  insertion: "Insertion",
  nearest: "Nearest",
  parallel: "Parallel",
  extension: "Extension",
};

/**
 * Map an `OsnapMode` to the SnapMarker mode (which uses different spelling for
 * `appint` and `geomcenter`). The marker registry only has 14 distinct glyphs
 * — we map the resolver's logical modes onto the marker dictionary.
 */
export function osnapModeToMarkerMode(m: OsnapMode): SnapMarkerMode {
  switch (m) {
    case "appint":
      return "apparent-intersection";
    case "geomcenter":
      return "geometric-center";
    default:
      return m;
  }
}

/** Shared empty set for the "nothing globally hidden" hover case. */
const EMPTY_HIDDEN_LAYERS: ReadonlySet<string> = new Set<string>();

/** Inputs for the pure snap/hover step. */
export interface PointerSnapStepArgs {
  /** Paper-world cursor (the `clientToWorld` result against the paper camera). */
  readonly rawWorld: Vec2;
  /** Cursor client position (passed through to `resolveObjectSnap`). */
  readonly cursorClient: { x: number; y: number };
  /** Resolved active input space (from `resolveActiveInputSpace`). */
  readonly space: ActiveInputSpace;
  /** Live OSNAP bindings (`osnapBindingsRef.current`); null disables snap. */
  readonly osnap: OsnapBindings | null;
  /** Live spatial index (`spatialIndexRef.current`); null disables both paths. */
  readonly spatialIndex: SpatialIndex | null;
  /** Hover tolerance in CSS pixels (`DRAG_THRESHOLD_PX`). */
  readonly dragThresholdPx: number;
  /** Whether the caller has an `onCanvasHover` binding (skips the hover work if not). */
  readonly wantHover: boolean;
  /**
   * Codex PR #784 round 4 F2 — the GLOBAL off / frozen layer set
   * (`hiddenLayersRef.current`, the same one `handleClickPick` reads). Used to
   * build the INSERT child-geometry filter below so hover and click agree on
   * whether an INSERT is under the cursor: without it, hovering where a HIDDEN
   * child of an INSERT would have been highlighted the INSERT even though
   * clicking the same point rejects it. `null` / omitted → nothing is globally
   * hidden (the filter then depends on the VP-frozen set alone).
   */
  readonly hiddenLayers?: ReadonlySet<string> | null;
}

/** Stacked-candidate descriptor for the SELECTIONCYCLING badge. */
export interface StackedCandidate {
  readonly id: string;
  readonly type: string;
  readonly layer: string;
}

/** Result of the pure snap/hover step. The caller applies the side-effects. */
export interface PointerSnapStepResult {
  /**
   * Cursor world coord in `space.spaceId` — cache to `lastSnappedWorld` and
   * pass to `onCanvasHover` (alongside the dispatched `activeSpaceId`, which is
   * "model" inside a VP). Equals `space.toSpace(rawWorld)` (cursor) unless a
   * magnet-snap replaces it with the snap point.
   */
  readonly world: Vec2;
  /**
   * True IFF the emitted {@link world} was actually replaced by a magnet snap
   * (AUTOSNAP bit 4 set AND a hit resolved). Distinct from `snapHit != null`:
   * with the magnet bit OFF the resolver still returns a hit (for the marker /
   * tooltip) but `world` stays the raw cursor, so the emitted coordinate was
   * NOT magnetized. Consumers that must know whether the committed point landed
   * on snapped geometry (e.g. the ORTHO gate — object snap overrides ortho only
   * when the point actually went to the snap) MUST read this, not `snapHit`.
   */
  readonly magnetized: boolean;
  /** Raw resolver hit (in `space.spaceId`) or null. */
  readonly snapHit: {
    kind: OsnapMode;
    world: Vec2;
    ownerEntityId: string;
    /** Set only for an Extended (Apparent) Intersection override hit. */
    overrideMode?: ExtendedOverrideMode;
  } | null;
  /**
   * Marker to paint, in PAPER coords (the overlay glyph rides the paper
   * camera). null ⇒ caller clears the marker.
   */
  readonly snapTarget: { mode: SnapMarkerMode; world: { x: number; y: number } } | null;
  /** SnapTip label (AUTOSNAP tooltip bit) or null. */
  readonly snapTip: string | null;
  /**
   * Topmost hover-hit entity id (in the active space) or null. When a
   * running OSNAP is engaged with AUTOSNAP magnet on, this is REPLACED by
   * the snap target's owner id (`snapHit.ownerEntityId`) — see
   * {@link rawHoverHitId} for the pre-substitution value.
   */
  readonly hoverHitId: string | null;
  /**
   * The distance-refined topmost hit under the cursor BEFORE the
   * magnet-snap owner substitution ({@link hoverHitId}) is applied. Equals
   * `hoverHitId` whenever no magnet substitution occurred. Consumers that
   * need "what geometry is actually under the cursor" as a pure
   * proximity pick — e.g. Extended (Apparent) Intersection override
   * first-operand capture, which must select the CLICKED entity rather
   * than whatever a running snap happens to be magnetized to — MUST read
   * this field, not `hoverHitId`.
   */
  readonly rawHoverHitId: string | null;
  /**
   * Stacked candidates under the cursor, topmost-first (zOrder DESC, then
   * smallest-bbox-area on ties). length < 2 ⇒ caller clears the store.
   */
  readonly stackedCandidates: StackedCandidate[];
}

/**
 * Resolve OSNAP + the hover hit-test for one pointer move, routed through the
 * active input space. Pure — returns data only.
 */
export function pointerSnapStep(
  args: PointerSnapStepArgs,
): PointerSnapStepResult {
  const {
    rawWorld,
    cursorClient,
    space,
    osnap,
    spatialIndex,
    dragThresholdPx,
    wantHover,
  } = args;
  const hiddenLayers = args.hiddenLayers ?? null;

  // Cursor in the ACTIVE space: model coords inside a VP, identity outside.
  const cursorSpaceWorld = space.toSpace(rawWorld);
  // ...and the same cursor in the SPATIAL-INDEX frame. The index is uniformly
  // OFFSET-world (`state/spatialBuilder.ts buildSpatialRecordsForSpaces`),
  // while `toSpace` on the REDIRECTED branch is `paperToModel`, whose result
  // is a DOCUMENT-frame model point — one whole session origin away. Every
  // index query below (`resolveObjectSnap`, the hover `intersect`, the
  // distance refinement against candidate GEOMETRY) takes THIS point; every
  // value that comes back out of the index is mapped back with
  // `fromOffsetWorld` before it meets `fromSpace` / `toDocument` / the emit.
  // Identity branch → both are no-ops and this is byte-equivalent to before.
  // See `ActiveInputSpace.toOffsetWorld`.
  const cursorIndexWorld = space.toOffsetWorld(cursorSpaceWorld);

  // Per-VP frozen-layer set (model entities frozen IN THE ACTIVE VP are
  // invisible through it → must be un-snappable / un-hoverable, AutoCAD
  // parity). Only populated when redirected (`space.viewport`); identity → null.
  const vpFrozenLayers = space.viewport?.frozenLayers;
  const vpFrozenSet =
    vpFrozenLayers && vpFrozenLayers.length > 0
      ? new Set(vpFrozenLayers)
      : null;

  /**
   * True when an entity is a valid in-space candidate: its space passes
   * `candidateEligible` (inside a VP only model space; identity only the
   * fallback space) AND its layer is not frozen in the active VP. Shared by the
   * snap getter wrapper and the hover hit-test so both paths filter identically.
   */
  const candidatePasses = (entity: RenderableEntity): boolean =>
    space.candidateEligible(entity.spaceId) &&
    !(vpFrozenSet !== null && vpFrozenSet.has(entity.layer));

  let world: Vec2 = cursorSpaceWorld;
  let magnetized = false;
  let snapHit: PointerSnapStepResult["snapHit"] = null;
  let snapTarget: PointerSnapStepResult["snapTarget"] = null;
  let snapTip: string | null = null;

  const magnetOn = osnap != null && (osnap.autosnap & AUTOSNAP_MAGNET) !== 0;

  // OSNAP-OVERRIDE-MODES — a captured Extended (Apparent) Intersection override
  // must resolve even when the running snaps are empty (OSMODE = 0 / suppressed):
  // an explicit object-snap override works while running OSNAP is off (AutoCAD).
  const extendedOverrideArmed =
    osnap != null &&
    osnap.extendedOverride != null &&
    osnap.extendedOverride.firstOwnerId !== null;

  if (osnap && spatialIndex && (osnap.activeModes.size > 0 || extendedOverrideArmed)) {
    // Wrap `getEntityById` so the resolver only ever sees in-space, non-VP-
    // frozen candidates. `resolveObjectSnap` skips ids whose getter returns
    // `undefined`, so a paper-space entity (inside a VP) or a VP-frozen one is
    // transparently dropped — zero API change to the resolver.
    const filteredGetEntityById = (id: string): RenderableEntity | undefined => {
      const entity = osnap.getEntityById(id);
      if (!entity) return undefined;
      return candidatePasses(entity) ? entity : undefined;
    };
    const hit = resolveObjectSnap({
      // Snap is resolved in the ACTIVE space: cursor + aperture in model coords
      // inside a VP, paper coords outside. `space.pixelsPerWorld` sizes the
      // aperture so it feels identical at any VP zoom (NOT the paper ppw).
      cursorWorld: cursorIndexWorld,
      cursorClient,
      pixelsPerWorld: space.pixelsPerWorld,
      aperturePx: osnap.aperturePx,
      activeModes: osnap.activeModes,
      spatialIndex,
      getEntityById: filteredGetEntityById,
      // OSNAP-OVERRIDE-MODES — thread the live Extended (Apparent) Intersection
      // override; the resolver honours it (and its captured first operand) here.
      extendedOverride: osnap.extendedOverride ?? null,
    });
    if (hit) {
      // `hit.world` is derived from candidate GEOMETRY, i.e. it comes back in
      // the OFFSET-world INDEX frame. Map it into `space.spaceId` once, here,
      // so everything downstream — `fromSpace` (marker), `toDocument` (the
      // hover emit / draft commit), the ORTHO re-resolve's own
      // `space.fromSpace(snapHit.world)` VP re-gate, and the OTRACK
      // acquired-point store — sees ONE frame. Identity branch → a no-op.
      const hitSpaceWorld = space.fromOffsetWorld(hit.world);
      snapHit = {
        kind: hit.kind,
        world: hitSpaceWorld,
        ownerEntityId: hit.ownerEntityId,
        overrideMode: hit.overrideMode,
      };
      const markerOn = (osnap.autosnap & AUTOSNAP_MARKER) !== 0;
      const tooltipOn = (osnap.autosnap & AUTOSNAP_TOOLTIP) !== 0;
      if (magnetOn) {
        // Replace the cursor world with the snapped point so the DI live coord,
        // crosshair anchor, and the next click all use the snapped target. Stays
        // in `space.spaceId` so it matches the dispatched `activeSpaceId`.
        world = hitSpaceWorld;
        magnetized = true;
      }
      if (markerOn) {
        // The overlay glyph rides the PAPER camera, so store PAPER coords:
        // `fromSpace` maps model→paper inside a VP, identity outside.
        snapTarget = {
          mode: osnapModeToMarkerMode(hit.kind),
          world: space.fromSpace(hitSpaceWorld),
        };
        // Extended override hits reuse the base glyph (intersection / apparent
        // intersection) but show the distinct override label ("Extended
        // Intersection" / "Extended Apparent Intersection") in the SnapTip.
        snapTip = tooltipOn
          ? hit.overrideMode
            ? EXTENDED_OVERRIDE_LABEL[hit.overrideMode]
            : OSNAP_MODE_LABEL[hit.kind]
          : null;
      }
    }
  }

  // ── Hover hit-test ─────────────────────────────────────────────
  let hoverHitId: string | null = null;
  let rawHoverHitId: string | null = null;
  let stackedCandidates: StackedCandidate[] = [];
  if (wantHover && spatialIndex) {
    // Tolerance in ACTIVE-space world units: CSS px / model ppw inside a VP,
    // / paper ppw outside — byte-identical to the legacy paper tolerance for any
    // real camera frustum (right-left > 1e-12, where `space.pixelsPerWorld ===
    // paperPpw` in the identity case). Only a degenerate/zero-width frustum
    // (clamped to 1e-12 upstream) would diverge.
    const tol = dragThresholdPx / Math.max(space.pixelsPerWorld, 1e-12);
    // ONE intersect(bbox) shared by the hover pick + the stacked list.
    const candidateIds = spatialIndex.intersect({
      left: cursorIndexWorld.x - tol,
      top: cursorIndexWorld.y - tol,
      right: cursorIndexWorld.x + tol,
      bottom: cursorIndexWorld.y + tol,
    });
    // Resolve + filter by the SAME predicate as snap (candidateEligible +
    // VP-frozen). Inside a VP this drops paper entities (which share the index)
    // and VP-frozen layers; identity → candidateEligible is always true for the
    // fallback space, so only cross-space hidden buckets are dropped.
    const resolved: RenderableEntity[] = [];
    for (const id of candidateIds) {
      const entity = spatialIndex.resolve(id);
      if (entity && candidatePasses(entity)) resolved.push(entity);
    }

    // Distance-refined topmost pick (matches the legacy `pickWithDistance`):
    // bbox prefilter already done by `intersect`; walk DRAWORDER order
    // (zOrder ASC, id ASC) and refine via `distanceToRenderable` so the hover
    // fires only when the cursor is within tolerance of the actual stroke, not
    // just inside the bbox. We feed `refinePickCandidates` an explicit
    // candidate array (vs reusing `pick(world, tol, index)`) because `pick`
    // re-queries the index WITHOUT the eligibility filter, which inside a VP
    // would let a paper entity win.
    const sortedForPick = [...resolved].sort((a, b) => {
      if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    // Codex PR #784 round 4 F2 — the SAME "is this INSERT child currently
    // DRAWN" predicate `handleClickPick` builds (global off / frozen ∪ this
    // viewport's frozen set). An INSERT is ONE spatial-index entity whose
    // `childRenderables` are its strokes, so without this the hover measured
    // against children that draw NOTHING and highlighted the reference where
    // the click path (which already filters) reports a miss. `null` when
    // nothing is hidden — the per-child check is then skipped entirely.
    const childFilter = makeVisibleChildGeometryFilter(
      hiddenLayers ?? EMPTY_HIDDEN_LAYERS,
      vpFrozenSet,
    );
    // ONE refinement walk for BOTH the hover hit and the stacked list (round 4
    // F3's rule, shared with the click path): each candidate's analytic
    // distance is computed exactly once.
    const refined = refinePickCandidates(
      // Candidate GEOMETRY is offset-world, so the distance walk must be too.
      cursorIndexWorld,
      tol,
      sortedForPick,
      childFilter,
    );
    if (refined.hit) hoverHitId = refined.hit.id;
    // Snapshot the pure proximity pick BEFORE the magnet-owner substitution
    // below can overwrite `hoverHitId`.
    rawHoverHitId = hoverHitId;

    // Stacked candidates for the SELECTIONCYCLING badge: topmost-first
    // (zOrder DESC, smallest-bbox-area wins on ties — the same rule
    // `SpatialIndex.pick` uses). length < 2 ⇒ caller clears the store.
    // Codex PR #784 round 4 F3 — over the DISTANCE-REFINED survivors, not
    // every broad-phase bbox candidate, so the badge counts what the cursor
    // actually touches (identical rule to the click path's cycling list).
    if (refined.within.length >= 2) {
      const ordered = [...refined.within].sort(compareTopmostFirst);
      stackedCandidates = ordered.map((c) => ({
        id: c.id,
        type: c.geometry.kind,
        layer: c.layer,
      }));
    }

    // Prefer the OSNAP-target's owner id when an active snap is engaged with
    // magnet on — keeps the hover highlight in sync with the snapped entity.
    if (snapHit && magnetOn) {
      hoverHitId = snapHit.ownerEntityId;
    }
  }

  return {
    world,
    magnetized,
    snapHit,
    snapTarget,
    snapTip,
    hoverHitId,
    rawHoverHitId,
    stackedCandidates,
  };
}
